import React from 'react';
import { Box } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { StatementXmlPreviewProps } from './StatementXmlPreview.types';
import type { StatementNodeData, StatementSegment, StatementSentenceRow } from './types';

const CLP_CODE_PATTERN = /^((EUH|H|P)\d{3}[A-Z]?)(\+(EUH|H|P)\d{3}[A-Z]?)*$/i;

export const isStandardClpCode = (code: string | undefined): boolean => {
  if (!code) return false;
  const normalized = code.trim().replace(/\s+/g, '').toUpperCase();
  return CLP_CODE_PATTERN.test(normalized);
};

export const uniqueOrderedCodes = (rows: StatementSentenceRow[], requireStandardCode = false): string[] => {
  const codes: string[] = [];

  rows.forEach((row) => {
    const code = row.code;
    if (!code) return;
    if (requireStandardCode && !isStandardClpCode(code)) return;
    if (!codes.includes(code)) codes.push(code);
  });

  return codes;
};

export const getNodeHoverBg = (theme: Theme, node: StatementNodeData): string => {
  if (node.type === 'GlossaryStatementNode') return alpha(theme.palette.warning.main, 0.18);
  return alpha(theme.palette.primary.main, 0.12);
};

export const getNodeFocusRing = (theme: Theme, node: StatementNodeData): string => {
  if (node.type === 'GlossaryStatementNode') return alpha(theme.palette.warning.main, 0.55);
  return alpha(theme.palette.primary.main, 0.55);
};

export const segmentKey = (segment: StatementSegment, index: number): string => {
  if (segment.kind === 'text') return `t-${index}`;
  return `n-${segment.node.id ?? 'noid'}-${index}`;
};

export const getClickableSentenceProps = (
  onNodeClick: StatementXmlPreviewProps['onNodeClick'],
  node: StatementNodeData | null
): Pick<React.ComponentProps<typeof Box>, 'role' | 'tabIndex' | 'onClick'> => {
  if (!onNodeClick || !node) return {};
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onNodeClick(node),
  };
};
